import { useEffect, useState } from "react";
import { History, User } from "lucide-react";
import dayjs from "dayjs";

import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { ATTENDANCE_CORRECTION_HISTORY } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

const AttendanceCorrectionHistory = ({ employeeId, fromDate, toDate }) => {
    const { fetch } = useApiMethods();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (!employeeId) return;

        const loadHistory = async () => {
            setLoading(true);
            try {
                const filters = { employee_id: employeeId };
                if (fromDate) filters.from_date = fromDate;
                if (toDate) filters.to_date = toDate;
                const apiData = await fetch(ATTENDANCE_CORRECTION_HISTORY, filters);
                if (apiData?.status === "success") {
                    setRows(apiData.data || []);
                } else {
                    ToastMsg("error", apiData?.message || "Failed to load correction history");
                }
            } catch (error) {
                ToastMsg("error", error.message);
            } finally {
                setLoading(false);
            }
        };

        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId, fromDate, toDate]);

    if (!employeeId) {
        return (
            <p className="text-sm text-muted-foreground">Select an employee to view correction history.</p>
        );
    }

    if (loading && rows.length === 0) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
                <History className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-muted-foreground">No attendance corrections recorded for this period.</p>
            </div>
        );
    }

    return (
        <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
            {rows.map((item) => (
                <div
                    key={item.id}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 p-3"
                >
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-semibold text-sidebar-primary">
                            {item.correction_type_label || item.correction_type}
                        </span>
                        <span className="text-xs text-muted-foreground">
                            {item.correction_date ? dayjs(item.correction_date).format("DD-MM-YYYY") : "-"}
                        </span>
                    </div>
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                        {item.reason}
                    </p>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div>
                            <span className="font-medium">Before: </span>
                            {item.old_status || "-"}
                            {item.old_check_in ? ` · In ${dayjs(item.old_check_in).format("h:mm A")}` : ""}
                            {item.old_check_out ? ` · Out ${dayjs(item.old_check_out).format("h:mm A")}` : ""}
                        </div>
                        <div>
                            <span className="font-medium">After: </span>
                            {item.new_status || "-"}
                            {item.new_check_in ? ` · In ${dayjs(item.new_check_in).format("h:mm A")}` : ""}
                            {item.new_check_out ? ` · Out ${dayjs(item.new_check_out).format("h:mm A")}` : ""}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                            <User className="h-3.5 w-3.5" />
                            {item.corrected_by_name || "Unknown"}
                        </span>
                        <span>{item.corrected_at || "-"}</span>
                        <span className={cn("capitalize")}>{item.source_label || item.source}</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AttendanceCorrectionHistory;
